/**
 * Compares two objects for equality.
 * The function is based on this article: https://dmitripavlutin.com/how-to-compare-objects-in-javascript/
 *
 * @param object1
 * @param object2
 * @returns true if the objects are equal, otherwise false
 */
export function deepEqual<T>(object1: T | undefined, object2: T | undefined): boolean {
  const object1UndefinedOrNull = object1 === undefined || object1 === null;
  const object2UndefinedOrNull = object2 === undefined || object2 === null;

  if (object1UndefinedOrNull || object2UndefinedOrNull) {
    if (object1 === object2) {
      return true;
    }
    return false;
  }

  const keys1 = Object.keys(object1);
  const keys2 = Object.keys(object2);

  if (keys1.length !== keys2.length) {
    return false;
  } else if (keys1.length === 0 && keys2.length === 0) {
    if (typeof object1 === 'object' && typeof object2 === 'object') {
      return true;
    }
    return object1 === object2;
  }

  for (const key of keys1) {
    const val1 = object1[key as keyof typeof object1];
    const val2 = object2[key as keyof typeof object2];
    const areObjects = isObject(val1) && isObject(val2);
    if ((areObjects && !deepEqual(val1, val2)) || (!areObjects && val1 !== val2)) {
      return false;
    }
  }

  return true;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isObject(object: any) {
  return object != null && typeof object === 'object';
}
